import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, errMessage, fileAuthUrl } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Card, StatusBadge, ServiceTags, STATUS_ORDER, STATUS_META, formatDate } from "@/pages/portal/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ArrowLeft, FileText, Upload, Loader2, Save, Paperclip, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = { electric_bill: "Electric bill", gas_bill: "Gas bill", other_bill: "Other document" };

export default function ApplicationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [label, setLabel] = useState("electric_bill");

  const canManage = ["admin", "manager", "rep"].includes(user.role);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/portal/applications/${id}`);
      setApp(data.application);
      setStatus(data.application.status);
      setNotes(data.application.notes || "");
    } catch (e) {
      toast.error(errMessage(e));
      navigate("/portal/applications");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const saveChanges = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/portal/applications/${id}`, { status, notes });
      setApp(data.application);
      toast.success("Application updated");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      await api.post(`/api/portal/applications/${id}/files`, fd);
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const submitToProcessing = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/api/portal/applications/${id}/submit`);
      setApp(data.application);
      toast.success(`Submitted — ${data.attachments} file(s) emailed to the processing team`);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-sm text-slatey">Loading…</div>;
  if (!app) return null;

  const dm = app.decision_maker || {};

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="application-detail">
      <Link to={user.role === "customer" ? "/portal" : "/portal/applications"} className="inline-flex items-center gap-1.5 text-sm text-slatey hover:text-navy">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-navy">{app.company_name}</h2>
          <p className="text-sm text-slatey">{app.state_name} · Submitted {formatDate(app.created_at)}{app.rep_name ? ` · Rep: ${app.rep_name}` : ""}</p>
        </div>
        <StatusBadge status={app.status} />
      </div>

      {/* Submit to processing */}
      {canManage && (
        <Card
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 p-5",
            app.processing_submitted ? "border-emerald-200 bg-emerald-50/60" : "border-electric/30 bg-electric/5"
          )}
          data-testid="submit-processing-card"
        >
          <div className="flex items-start gap-3">
            {app.processing_submitted ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
            ) : (
              <Send className="mt-0.5 h-5 w-5 text-electric" />
            )}
            <div>
              <p className="text-sm font-semibold text-navy">
                {app.processing_submitted ? "Submitted to processing" : "Ready to submit?"}
              </p>
              <p className="text-xs text-slatey">
                {app.processing_submitted
                  ? `Sent on ${formatDate(app.submitted_at)}. Resending emails the details and all uploaded files again.`
                  : "Emails the application details and every uploaded bill/document to the processing team."}
              </p>
            </div>
          </div>
          <Button
            onClick={submitToProcessing}
            disabled={submitting}
            className={app.processing_submitted ? "bg-navy text-white hover:bg-navy/90" : "bg-electric text-white hover:bg-electric-hover"}
            data-testid="submit-processing-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="mr-1.5 h-4 w-4" /> {app.processing_submitted ? "Resend" : "Submit application"}
              </>
            )}
          </Button>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-3 font-heading text-sm font-semibold text-navy">Decision maker</h3>
          <dl className="space-y-2 text-sm">
            <Row label="Name" value={dm.name} />
            <Row label="Title" value={dm.title || "—"} />
            <Row label="Email" value={dm.email} />
            <Row label="Phone" value={dm.phone} />
          </dl>
        </Card>
        <Card className="p-5">
          <h3 className="mb-3 font-heading text-sm font-semibold text-navy">Details</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4"><dt className="text-slatey">Services</dt><dd><ServiceTags services={app.services} /></dd></div>
            <Row label="Agreement expires" value={formatDate(app.agreement_expiration)} />
            <Row label="State" value={app.state_name} />
            <Row label="Submitted by" value={app.submitted_by_role} />
          </dl>
        </Card>
      </div>

      {/* Files */}
      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold text-navy">Bills & documents</h3>
          <div className="flex items-center gap-2">
            <select value={label} onChange={(e) => setLabel(e.target.value)} data-testid="upload-label" className="rounded-md border border-line bg-white px-2 py-1.5 text-xs text-navy">
              <option value="electric_bill">Electric bill</option>
              <option value="gas_bill">Gas bill</option>
              <option value="other_bill">Other document</option>
            </select>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-medium text-white hover:bg-navy/90" data-testid="upload-file-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
              <input type="file" className="hidden" onChange={uploadFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" disabled={uploading} />
            </label>
          </div>
        </div>
        {app.files.length === 0 ? (
          <p className="py-4 text-center text-sm text-slatey">No files uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-line" data-testid="file-list">
            {app.files.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Paperclip className="h-4 w-4 shrink-0 text-slatey" />
                  <span className="truncate text-sm text-navy">{f.original_filename}</span>
                  <span className="shrink-0 rounded bg-ice px-1.5 py-0.5 text-[11px] text-slatey">{CATEGORY_LABEL[f.label] || f.label}</span>
                </div>
                <a href={fileAuthUrl(f.url)} target="_blank" rel="noreferrer" className="shrink-0 text-xs font-medium text-electric hover:underline" data-testid={`view-file-${f.id}`}>
                  View
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Management */}
      {canManage && (
        <Card className="p-5" data-testid="manage-panel">
          <h3 className="mb-3 font-heading text-sm font-semibold text-navy">Manage</h3>
          <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <label className="mb-1 block text-xs font-medium text-slatey">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select" className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-navy">
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-slatey">Internal notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="notes-textarea" className="bg-white" />
          </div>
          <Button onClick={saveChanges} disabled={saving} className="mt-4 bg-electric text-white hover:bg-electric-hover" data-testid="save-application">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
          </Button>
        </Card>
      )}

      {/* Status timeline */}
      <Card className="p-5">
        <h3 className="mb-3 font-heading text-sm font-semibold text-navy">Progress</h3>
        <ol className="space-y-3">
          {(app.status_history || []).map((h, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`h-2 w-2 rounded-full ${STATUS_META[h.status]?.dot || "bg-slate-400"}`} />
              <span className="font-medium text-navy">{STATUS_META[h.status]?.label || h.status}</span>
              <span className="text-slatey">{formatDate(h.at)}</span>
            </li>
          ))}
        </ol>
        {!canManage && (
          <div className="mt-4 rounded-md bg-ice p-3 text-xs text-slatey">
            <p className="font-medium text-navy">What happens next</p>
            <p className="mt-1">1. We review your bill and information. 2. You'll receive a Letter of Authorization (LOA) by email. 3. We send a proposal outlining your savings. 4. You execute a contract at your new rate.</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slatey">{label}</dt>
      <dd className="text-right font-medium text-navy">{value}</dd>
    </div>
  );
}
